import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Translate, ArrowLeft, ChartLineUp, Lock } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

const PROVIDER_INFO = {
  "claude-sonnet": { name: "Claude Sonnet 4.6", tag: "Qualité max", speed: "~3-5s", note: "Meilleure fidélité, plus lent. Emergent LLM Key." },
  "claude-haiku":  { name: "Claude Haiku 4.5",  tag: "Rapide",      speed: "~1-2s", note: "Excellent compromis vitesse/qualité. Emergent LLM Key." },
  "gpt-mini":      { name: "GPT-5.4 Mini",      tag: "Alternative", speed: "~1-2s", note: "Alternative OpenAI rapide. Emergent LLM Key." },
  "google":        { name: "Google Translate",  tag: "Économique",  speed: "~0.5s", note: "Le plus rapide. Nécessite une clé API Google Cloud (payante)." },
};

function fmtEur(v) {
  const n = typeof v === "number" ? v : Number(v);
  if (v == null || !Number.isFinite(n)) return "—";
  if (n < 0.01) return n.toFixed(4).replace(".", ",") + " €";
  return n.toFixed(2).replace(".", ",") + " €";
}

function fmtMonth(ym) {
  // "2026-02" → "février 2026"
  try {
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  } catch { return ym; }
}

export default function TranslationModule() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [enabledForTech, setEnabledForTech] = useState(false);
  const [autoDetect, setAutoDetect] = useState(false);
  const [active, setActive] = useState([]);
  const [supported, setSupported] = useState([]);
  const [provider, setProvider] = useState("claude-sonnet");
  const [providers, setProviders] = useState([]);
  const [rates, setRates] = useState({});
  const [googleKey, setGoogleKey] = useState("");
  const [googleKeySet, setGoogleKeySet] = useState(false);
  const [usage, setUsage] = useState(null);
  const [canEditProvider, setCanEditProvider] = useState(false);
  const [monthlyBudget, setMonthlyBudget] = useState(0);
  const [budgetAlertSentMonth, setBudgetAlertSentMonth] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/translate/config"),
      api.get("/translate/usage").catch(() => ({ data: null })),
    ]).then(([cfg, u]) => {
      setEnabled(!!cfg.data.enabled);
      setEnabledForTech(!!cfg.data.enabled_for_tech);
      setAutoDetect(!!cfg.data.auto_detect_client_lang);
      setActive(cfg.data.active_languages || []);
      setSupported(cfg.data.supported || []);
      setProvider(cfg.data.provider || "claude-sonnet");
      setProviders(cfg.data.supported_providers || []);
      setRates(cfg.data.rates_eur_per_1000_chars || {});
      setGoogleKeySet(!!cfg.data.google_api_key_set);
      setGoogleKey("");
      setUsage(u.data);
      setCanEditProvider(!!cfg.data.can_edit_provider);
      setMonthlyBudget(Number(cfg.data.monthly_budget_eur || 0));
      setBudgetAlertSentMonth(cfg.data.budget_alert_sent_month || "");
    })
    .catch((e) => toast.error("Chargement impossible : " + (e?.response?.data?.detail || e.message)))
    .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleLang = (code, on) => {
    setActive((prev) => {
      const s = new Set(prev);
      if (on) s.add(code); else s.delete(code);
      return Array.from(s);
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Tech saves only `active_languages` server-side (ignored fields kept
      // for API shape). Admin gets the full payload.
      await api.put("/translate/config", {
        enabled,
        active_languages: active,
        auto_detect_client_lang: autoDetect,
        provider,
        google_api_key: googleKey || "",
        monthly_budget_eur: Number(monthlyBudget || 0),
      });
      toast.success(isAdmin ? "Configuration enregistrée" : "Langues actives enregistrées");
      load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const isGoogleUsable = googleKeySet || !!googleKey;
  const monthlyTotal = Number(usage?.total_cost_eur ?? 0);
  const monthlyCount = usage?.total_count ?? 0;
  const history = usage?.history || [];
  // v282 — budget progress: 0 = feature disabled. `monthlyBudget` may be a
  // string (fresh from the <input>), coerce for maths.
  const budgetNum = Number(monthlyBudget || 0);
  const budgetActive = budgetNum > 0;
  const budgetPercent = budgetActive ? Math.min(100, Math.round((monthlyTotal / budgetNum) * 100)) : 0;
  const overBudget = budgetActive && monthlyTotal >= budgetNum;
  const nearBudget = budgetActive && !overBudget && monthlyTotal / budgetNum >= 0.7;
  const barColor = overBudget ? "bg-red-500" : nearBudget ? "bg-orange-400" : "bg-emerald-500";
  const alertedThisMonth = !!budgetAlertSentMonth && usage?.month === budgetAlertSentMonth;

  return (
    <div className="p-6 max-w-4xl" data-testid="translation-module-page">
      <div className="mb-6 flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/modules" data-testid="back-to-modules"><ArrowLeft size={16} className="mr-1"/> Modules</Link>
        </Button>
      </div>
      <header className="mb-6">
        <h1 className="text-3xl font-display font-bold flex items-center gap-2">
          <Translate size={28}/> Module Langues
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {isAdmin
            ? "Traduction à la volée des tickets clients via drapeaux. Choisissez le moteur (Claude, GPT ou Google Translate) et suivez la consommation mensuelle. Le client ne voit jamais les drapeaux."
            : "Sélectionnez les langues à afficher au-dessus des messages tickets et consultez la consommation mensuelle. Le moteur IA et sa clé sont configurés par les administrateurs."}
        </p>
      </header>

      {loading ? (
        <div className="text-muted-foreground">Chargement…</div>
      ) : (
        <div className="space-y-6">
          {isAdmin && (
            <Card>
              <CardHeader><CardTitle className="font-display">Activation du module</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Switch id="translation-enabled" data-testid="translation-enabled"
                    checked={enabled} onCheckedChange={setEnabled}/>
                  <Label htmlFor="translation-enabled" className="cursor-pointer">
                    Activer les drapeaux de traduction sur les tickets
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Tant que ce module n'est pas activé, aucun drapeau n'apparaît sur les tickets.
                  Le toggle « Accessible aux techniciens » sur la page <Link to="/modules" className="underline">Modules</Link>
                  {" "}est actuellement <span className="font-semibold">{enabledForTech ? "activé" : "désactivé"}</span>.
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                Moteur de traduction
                {!canEditProvider && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid="provider-readonly-badge">
                    <Lock size={10}/> Lecture seule
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                {canEditProvider
                  ? <>Le moteur choisi sert à la fois pour <strong>traduire</strong> les messages ET pour <strong>détecter</strong> la langue du client (auto-détection).</>
                  : <>Moteur actuellement utilisé pour traduire et détecter la langue des tickets. Contactez un administrateur pour le modifier.</>}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {providers.map((p) => {
                  const info = PROVIDER_INFO[p] || { name: p };
                  const rate = rates[p];
                  const clickDisabled = !canEditProvider || (p === "google" && !isGoogleUsable);
                  const selected = provider === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      disabled={clickDisabled}
                      onClick={() => canEditProvider && setProvider(p)}
                      data-testid={`translation-provider-${p}`}
                      className={`text-left rounded-lg border p-3 transition-colors
                        ${selected ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border"}
                        ${!canEditProvider ? "cursor-not-allowed" : (clickDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-muted/50")}
                        ${!canEditProvider && !selected ? "opacity-70" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="font-semibold">{info.name}</div>
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted">{info.tag}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        Vitesse {info.speed} · {rate != null ? `${fmtEur(rate)}/1000 caractères` : "tarif inconnu"}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">{info.note}</div>
                      {canEditProvider && p === "google" && !isGoogleUsable && (
                        <div className="text-[11px] text-orange-600 dark:text-orange-400 mt-1">
                          Renseignez une clé API Google Cloud ci-dessous pour activer ce choix.
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {canEditProvider ? (
                <div className="pt-2 border-t space-y-2">
                  <Label htmlFor="google-key" className="text-sm">Clé API Google Cloud Translation (optionnelle)</Label>
                  <Input id="google-key" type="password" data-testid="google-api-key"
                    placeholder={googleKeySet ? "•••••••• (clé enregistrée, laissez vide pour ne pas la changer)" : "AIzaSy…"}
                    value={googleKey} onChange={(e) => setGoogleKey(e.target.value)}/>
                  <p className="text-[11px] text-muted-foreground">
                    Obtenez une clé sur <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="underline">console.cloud.google.com</a>{" "}
                    après avoir activé l'API « Cloud Translation ». Facturation à l'usage (~0,018 €/1000 caractères).
                  </p>
                </div>
              ) : (
                <div className="pt-2 border-t text-[11px] text-muted-foreground" data-testid="google-key-readonly">
                  Clé Google Cloud : {googleKeySet ? "renseignée par l'administrateur" : "non renseignée"}.
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <ChartLineUp size={18}/> Consommation ce mois
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!usage ? (
                <div className="text-sm text-muted-foreground">Aucune donnée pour le moment.</div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-baseline gap-6" data-testid="translation-usage-summary">
                    <div>
                      <div className="text-xs text-muted-foreground">Traductions</div>
                      <div className="text-2xl font-semibold">{monthlyCount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Coût estimé</div>
                      <div className="text-2xl font-semibold">{fmtEur(monthlyTotal)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Mois</div>
                      <div className="text-lg font-mono">{usage.month}</div>
                    </div>
                  </div>
                  {Object.keys(usage.by_provider || {}).length > 0 && (
                    <div className="rounded-md border">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-xs">
                          <tr>
                            <th className="text-left px-3 py-2">Moteur</th>
                            <th className="text-right px-3 py-2">Appels</th>
                            <th className="text-right px-3 py-2">Caractères</th>
                            <th className="text-right px-3 py-2">Coût</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(usage.by_provider).map(([p, v]) => (
                            <tr key={p} className="border-t">
                              <td className="px-3 py-2">{PROVIDER_INFO[p]?.name || p}</td>
                              <td className="text-right px-3 py-2 font-mono">{v.count}</td>
                              <td className="text-right px-3 py-2 font-mono">{v.chars.toLocaleString("fr-FR")}</td>
                              <td className="text-right px-3 py-2 font-mono">{fmtEur(v.cost_eur)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {budgetActive && (
                    <div className="rounded-md border p-3" data-testid="translation-budget-progress">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium">
                          Budget mensuel {overBudget && <span className="text-red-600 font-semibold">— dépassé</span>}
                          {nearBudget && <span className="text-orange-600 font-semibold">— proche du seuil</span>}
                        </span>
                        <span className="font-mono">
                          {fmtEur(monthlyTotal)} / {fmtEur(budgetNum)}
                        </span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`} style={{ width: `${budgetPercent}%` }} data-testid="translation-budget-bar"/>
                      </div>
                      {alertedThisMonth && (
                        <div className="mt-2 text-[11px] text-red-600 dark:text-red-400" data-testid="translation-budget-alerted">
                          Alerte envoyée aux administrateurs pour ce mois.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <ChartLineUp size={18}/> Historique par mois (6 derniers)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border overflow-x-auto" data-testid="translation-history">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs">
                      <tr>
                        <th className="text-left px-3 py-2">Mois</th>
                        <th className="text-right px-3 py-2">Traductions</th>
                        <th className="text-left px-3 py-2">Moteurs utilisés</th>
                        <th className="text-right px-3 py-2">Coût estimé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => {
                        const providers_used = Object.keys(h.by_provider || {});
                        return (
                          <tr key={h.month} className="border-t" data-testid={`translation-history-${h.month}`}>
                            <td className="px-3 py-2 capitalize">{fmtMonth(h.month)}</td>
                            <td className="text-right px-3 py-2 font-mono">{h.total_count}</td>
                            <td className="px-3 py-2 text-xs">
                              {providers_used.length === 0
                                ? <span className="text-muted-foreground">—</span>
                                : providers_used.map((p) => PROVIDER_INFO[p]?.name || p).join(" · ")}
                            </td>
                            <td className="text-right px-3 py-2 font-mono">{fmtEur(h.total_cost_eur)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">
                  Coûts estimés à partir des tarifs officiels du moteur choisi au moment de chaque appel
                  (Emergent LLM Key pour Claude/GPT, Google Cloud Translation pour Google).
                </p>
              </CardContent>
            </Card>
          )}

          {isAdmin && (
            <Card>
              <CardHeader><CardTitle className="font-display">Budget mensuel — alerte automatique</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <Label htmlFor="monthly-budget" className="whitespace-nowrap">Budget (€ / mois)</Label>
                  <Input
                    id="monthly-budget"
                    type="number"
                    step="0.01"
                    min="0"
                    value={monthlyBudget}
                    onChange={(e) => setMonthlyBudget(e.target.value)}
                    className="w-32"
                    data-testid="translation-monthly-budget"
                  />
                  <span className="text-xs text-muted-foreground">
                    (0 = alerte désactivée)
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Quand la consommation cumulée du mois dépasse ce seuil, une notification est envoyée
                  automatiquement à <strong>tous les administrateurs actifs</strong> dans la cloche (une seule
                  alerte par mois, réarmée automatiquement le 1<sup>er</sup> du mois suivant, ou immédiatement
                  si vous modifiez le seuil).
                </p>
              </CardContent>
            </Card>
          )}

          {isAdmin && (
            <Card>
              <CardHeader><CardTitle className="font-display">Détection automatique de la langue du client</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <Switch id="auto-detect" data-testid="translation-auto-detect"
                    checked={autoDetect} onCheckedChange={setAutoDetect} disabled={!enabled}/>
                  <Label htmlFor="auto-detect" className="cursor-pointer">
                    Détecter automatiquement la langue de la demande client
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Quand activé, à l'ouverture d'un ticket le moteur analyse la description client et met en surbrillance
                  le drapeau correspondant (« langue détectée »). Le technicien sait tout de suite dans quelle langue traduire.
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="font-display">Langues actives</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-4">
                Cochez les langues qui apparaîtront comme drapeaux au-dessus des messages tickets.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {supported.map((l) => {
                  const on = active.includes(l.id);
                  return (
                    <label key={l.id}
                      className="flex items-center gap-2 text-sm cursor-pointer rounded-md border px-3 py-1.5 hover:bg-muted"
                      data-testid={`translation-lang-${l.id}`}>
                      <input type="checkbox" checked={on}
                        onChange={(e) => toggleLang(l.id, e.target.checked)}/>
                      <span className="text-lg">{l.flag}</span>
                      <span>{l.name}</span>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="translation-save">
              {saving ? "Enregistrement…" : (isAdmin ? "Enregistrer" : "Enregistrer les langues")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
